
import { useEffect, useRef, useState } from 'react'
import { Group, Image as KonvaImage, Line, Rect } from 'react-konva'
import { ICON_MAP } from './icons'
import { SPAN_GEAR_TYPES } from './gear'
import { getGearSubtype } from './gear-subtypes'
import { GearElement } from './types'

/** Map our gear types to the icon IDs used in rescue-canvas/icons.ts */
export const GEAR_TO_ICON: Record<string, string> = {
  anchor: 'anchor',
  pulley: 'pulley',
  deviation: 'deviation',
  carabiner: 'carabiner',
  'rope-grab': 'rope-grab',
  descender: 'descender-custom',
  technician: 'technician',
  casualty: 'casualty-suspended',
  load: 'load',
  'i-beam-h': 'i-beam-h',
  'i-beam-v': 'i-beam-v',
  stretcher: 'stretcher',
  edge: 'edge',
  'edge-left': 'edge-left',
  'edge-right': 'edge-right',
  're-anchor': 're-anchor',
}

function useSvgImage(svgString: string | undefined, width: number, height: number): HTMLImageElement | null {
  const [image, setImage] = useState<HTMLImageElement | null>(null)
  const prevSrc = useRef<string>('')

  useEffect(() => {
    if (!svgString) return

    // Ensure the SVG has explicit width/height for rasterization
    const sized = svgString
      .replace(/width="[^"]*"/, `width="${width}"`)
      .replace(/height="[^"]*"/, `height="${height}"`)

    const blob = new Blob([sized], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)

    if (prevSrc.current) URL.revokeObjectURL(prevSrc.current)
    prevSrc.current = url

    const img = new window.Image()
    img.onload = () => setImage(img)
    img.src = url

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [svgString, width, height])

  return image
}

/**
 * Re-anchor span icon drawn with native Konva shapes.
 * End glyphs (left anchor plate + right anchor plate) stay at a fixed pixel
 * size derived from element height; only the connecting rope span stretches.
 */
function ReAnchorSpanIcon({ element }: { element: GearElement }) {
  const { width, height } = element
  const s = height / 64 // glyph scale factor (height-based, fixed)

  // Left anchor plate
  const plateW = 12 * s
  const plateH = 12 * s
  const plateX = 4 * s
  const plateY = 2 * s
  const plateR = 2 * s

  // Left stem
  const stemTopY = plateY + plateH
  const stemBotY = stemTopY + 8 * s
  const stemX = 10 * s

  // Right anchor plate (mirrored from left)
  const rPlateX = width - plateX - plateW
  const rStemX = width - stemX

  // Span rope: quadratic bezier from left stem bottom to right stem bottom
  const midX = width / 2
  const sagY = stemBotY + 4 * s

  return (
    <Group>
      {/* Left anchor plate */}
      <Rect
        x={plateX}
        y={plateY}
        width={plateW}
        height={plateH}
        cornerRadius={plateR}
        fill="#1D4ED8"
        stroke="#1E3A8A"
        strokeWidth={1.5}
      />
      {/* Left stem */}
      <Line
        points={[stemX, stemTopY, stemX, stemBotY]}
        stroke="#1D4ED8"
        strokeWidth={2.5}
        lineCap="round"
      />
      {/* Span rope (quadratic bezier approximated as polyline with sag) */}
      <Line
        points={[stemX, stemBotY, midX, sagY, rStemX, stemBotY]}
        stroke="#475569"
        strokeWidth={3}
        lineCap="round"
        tension={0.4}
      />
      {/* Right anchor plate */}
      <Rect
        x={rPlateX}
        y={plateY}
        width={plateW}
        height={plateH}
        cornerRadius={plateR}
        fill="#1D4ED8"
        stroke="#1E3A8A"
        strokeWidth={1.5}
      />
      {/* Right stem */}
      <Line
        points={[rStemX, stemTopY, rStemX, stemBotY]}
        stroke="#1D4ED8"
        strokeWidth={2.5}
        lineCap="round"
      />
    </Group>
  )
}

type GearIconProps = {
  element: GearElement
}

export function GearIcon({ element }: GearIconProps) {
  const subtype = element.subtype ? getGearSubtype(element.subtype) : null
  const iconId = (subtype?.iconKey && subtype.iconKey !== GEAR_TO_ICON[element.gearType])
    ? subtype.iconKey
    : GEAR_TO_ICON[element.gearType]
  const iconDef = iconId ? ICON_MAP[iconId] : undefined
  const image = useSvgImage(iconDef?.svg, element.width, element.height)

  // Span gear uses native Konva shapes to keep end glyphs fixed-size
  if (SPAN_GEAR_TYPES.has(element.gearType)) {
    return <ReAnchorSpanIcon element={element} />
  }

  if (!image) return <Group />

  return (
    <KonvaImage
      image={image}
      width={element.width}
      height={element.height}
    />
  )
}
